import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft";
import ReactTable from "../../components/ReactTable/ReactTable";
import { config } from "../../config";
import "../Payouts/payouts.css";
import "./DriverWallet.css";

const money = (value) => `${Number(value || 0).toFixed(2)}`;
const date = (value) => (value ? new Date(value).toLocaleString("en-IN") : "-");

export default function DriverWallet() {
  const { uid } = useParams();
  const { isSidebar } = useContext(SidebarContext);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}driver/walletHistory/${uid}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Unable to load wallet");
      setWallet(result.wallet);
    } catch (error) {
      console.error("Driver wallet fetch error:", error);
      setWallet(null);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);
  const rows = useMemo(
    () =>
      (wallet?.ledger || []).filter((item) => {
        const transactionId = String(item.transactionId || "").toUpperCase();

        const type = String(item.type || "").toLowerCase();

        let typeLabel = "";

        if (type === "admin commission deducted") {
          typeLabel = "admin commission";
        } else if (type === "credit") {
          typeLabel = transactionId.startsWith("RECH")
            ? "wallet recharge"
            : transactionId.startsWith("REFR")
              ? "referral bonus"
              : "credit earned";
        } else if (transactionId.startsWith("CANCEL")) {
          typeLabel = "cancellation fee";
        } else if (transactionId.startsWith("REFR")) {
          typeLabel = "referral bonus";
        } else if (transactionId.startsWith("RECH")) {
          typeLabel = "wallet recharge";
        } else {
          typeLabel = type;
        }

        const searchable = `
        ${item.transactionId || ""}
        ${item.type || ""}
        ${typeLabel}
        ${item.source || ""}
        ${item.reason || ""}
        ${item.referenceId || ""}
        ${item.status || ""}
      `.toLowerCase();

        return (
          (type === "all" || item.type === type) &&
          (status === "all" || item.status === status) &&
          (!query || searchable.includes(query.toLowerCase().trim()))
        );
      }),
    [wallet, query, type, status],
  );
  const columns = useMemo(
    () => [
      {
        accessorKey: "transactionId",
        header: "Transaction ID",
        size: 180,
        minSize: 180,
        Cell: ({ cell }) => (
          <span className="wallet-transaction-id">
            {cell.getValue() || "-"}
          </span>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        Cell: ({ cell, row }) => {
          const type = String(cell.getValue() || "").toLowerCase();
          const transactionId = String(
            row.original.transactionId || "",
          ).toUpperCase();

          let label = "-";
          let className = "text-danger";

          if (type === "admin commission deducted") {
            label = "Admin Commission";
            className = "text-danger";
          } else if (type === "credit") {
            if (transactionId.startsWith("PAYC")) {
              label = "Ride Earning";
            } else if (transactionId.startsWith("RECH")) {
              label = "Wallet Recharge";
            } else if (transactionId.startsWith("REFR")) {
              label = "Referral Bonus";
            } else {
              label = "Credit Earned";
            }

            className = "text-success";
          } else if (transactionId.startsWith("CANCEL")) {
            label = "Cancellation Fee";
            className = "text-danger";
          }
          return <span className={className}>{label}</span>;
        },
      },
      {
        accessorKey: "amount",
        header: "Amount (₹)",
        Cell: ({ cell, row }) => {
          const amount = Number(cell.getValue() || 0);
          const type = String(row.original.type || "").toLowerCase();

          const isCredit = type === "credit";
          const isDebit =
            type === "debit" || type === "admin commission deducted";
          const isCancelled = row.original.transactionId.startsWith("CANCEL")

          return (
            <span
              className={
                isCredit ? "text-success" : isDebit || isCancelled ? "text-danger" : ""
              }
            >
              {isCredit ? "+" : isDebit || isCancelled ? "- " : ""}
              {money(Math.abs(amount))}
            </span>
          );
        },
      },

      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell, row }) => {
          const status = String(cell.getValue() || "").toLowerCase();
          const type = String(row.original.type || "").toLowerCase();
          const transactionId = String(
            row.original.transactionId || "",
          ).toUpperCase();

          // PAYC - Admin Commission
          if (type === "admin commission deducted") {
            return "Deducted";
          }

          // PAYC - Ride Earning
          if (type === "credit" && transactionId.startsWith("PAYC")) {
            return "Earned";
          }

          // Wallet Recharge
          if (transactionId.startsWith("RECH")) {
            return "Credited";
          }

          if (transactionId.startsWith("CANCEL")) {
            return "Deducted";
          }

          // Referral Bonus
          if (transactionId.startsWith("REFR")) {
            return "Credited";
          }

          // Other debit
          if (type === "debit") {
            return "Deducted";
          }

          if (status === "success") {
            return "Earned";
          }

          return cell.getValue() || "-";
        },
      },
      // {
      //   accessorKey: "balanceBefore",
      //   header: "Before (₹)",
      //   Cell: ({ cell }) => money(cell.getValue()),
      // },
      // {
      //   accessorKey: "balanceAfter",
      //   header: "After (₹)",
      //   Cell: ({ cell }) => money(cell.getValue()),
      // },
      // { accessorKey: "source", header: "Source" },
      // { accessorKey: "referenceId", header: "Reference" },

      {
        accessorKey: "createdAt",
        header: "Date",
        Cell: ({ cell }) => date(cell.getValue()),
      },
    ],
    [],
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : 0 }}
              >
                <div className="main-content-inner">
                  <div className="wg-box">
                    <div className="flex items-center justify-between mb-4 gap-3">
                      <div>
                        <h5 className="fare-heading">
                          Driver Wallet — {wallet?.driverName || "Loading…"}
                        </h5>
                        <p className="text-secondary mb-0">
                          Immutable wallet ledger and current balance.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-outline-success"
                          onClick={load}
                        >
                          Refresh
                        </button>
                        <Link
                          className="btn btn-dark d-flex align-items-center"
                          to="/driver"
                        >
                          Drivers
                        </Link>
                      </div>
                    </div>
                    <div
                      className="dashboard-stats"
                      style={{ marginBottom: 20 }}
                    >
                      <Card
                        title="Current Balance"
                        value={money(wallet?.currentBalance)}
                      />
                      <Card
                        title="Credit / Negative Limit"
                        value={money(wallet?.creditLimit)}
                      />
                      <Card
                        title="Available Balance"
                        value={money(wallet?.availableBalance)}
                      />
                      <Card
                        title="Outstanding Negative"
                        value={money(wallet?.outstandingNegativeAmount)}
                      />
                      <Card
                        title="Total Credits"
                        value={money(wallet?.totalCredits)}
                      />
                      <Card
                        title="Total Debits"
                        value={money(wallet?.totalDebits)}
                      />
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <input
                        className="form-control wallet-filter-control"
                        style={{ maxWidth: 280 }}
                        placeholder="Search transaction or type"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                      {/* <select
                        className="form-select wallet-filter-control"
                        style={{ maxWidth: 150 }}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                      >
                        <option value="all">All types</option>
                        <option value="credit">Credits</option>
                        <option value="debit">Debits</option>
                      </select>
                      <select
                        className="form-select wallet-filter-control"
                        style={{ maxWidth: 170 }}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="all">All statuses</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                      </select> */}
                    </div>
                    {loading ? (
                      <WalletShimmer />
                    ) : (
                      <ReactTable columns={columns} data={rows} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, value }) {
  return (
    <div className="stat-card">
      <div className="stat-header">
        <h3>{title}</h3>
      </div>
      <p className="stat-value">{value}</p>
    </div>
  );
}
function WalletShimmer() {
  return (
    <div className="wallet-shimmer" aria-label="Loading wallet">
      <div className="wallet-shimmer-bar title" />
      {Array.from({ length: 7 }, (_, index) => (
        <div className="wallet-shimmer-bar" key={index} />
      ))}
    </div>
  );
}
